import { useMemo, useEffect, useState, useRef } from "react";
import {
	PropertyMappings,
	PropertyMappingSelectorProps,
	PropertyMappingSelectorViewModel,
	PropertyWithExamples,
} from "./PropertyMappingSelector.types";
import { extractAllPropertiesFromGeoData, autoGuessPropertyMappings } from "./PropertyMappingSelector.helpers";
import { DefaultImportTableColumns, ImportTableColumn } from "../../../../utilities/types";

/**
 * All mapping fields that can be auto-guessed.
 * Excludes 'uniqueKey' which is optional and handled differently.
 */
const MAPPING_FIELDS: (keyof PropertyMappings)[] = [
	"grower",
	"farm",
	"field",
	"fieldId",
	"labName",
	"labInstructions",
	"submissionNotes",
	"yieldGoal",
	"submitterName",
	"submitterEmail",
	"growerPhoneNumber",
	"eventId",
	"labSubmissionCode",
	"externalId",
	"externalFarmId",
	"externalLabId",
	"dateFieldReadyEstimated",
	"density",
	"depth",
	"frequencyOfMicros",
	"testPackage",
	"fieldPriority",
	"nextCrop",
	"lastCrop",
	"branch",
	"salesperson",
	"salespersonMobilePhone",
];

/**
 * Create an empty PropertyMappings object with all fields set to null.
 */
function createEmptyMappings(): PropertyMappings {
	const mappings: Partial<PropertyMappings> = {};
	for (const field of MAPPING_FIELDS) {
		mappings[field] = null;
	}
	return mappings as PropertyMappings;
}

export function usePropertyMappingSelectorViewModel(
	props: PropertyMappingSelectorProps,
): PropertyMappingSelectorViewModel {
	const {
		geoJsons,
		propertyMappings,
		onMappingChange,
		filterType,
		userSpecifiedFields,
		pendingGlobalMappings,
		company,
	} = props;

	// State to track if auto-guessing has occurred
	const [showAutoGuessNotice, setShowAutoGuessNotice] = useState(false);

	// Track fields the user has manually changed at this group level
	// This prevents auto-guess from overriding user deselections
	const localUserSpecifiedFields = useRef<Set<keyof PropertyMappings>>(new Set());

	// Extract properties from the provided GeoJSON data
	const headerProperties = useMemo(() => {
		if (!geoJsons || geoJsons.length === 0) return [];
		return extractAllPropertiesFromGeoData(geoJsons);
	}, [geoJsons]);

	const hasMapping = (mapping: string[] | null | undefined): boolean => {
		return Array.isArray(mapping) && mapping.length > 0;
	};

	// Auto-guess property mappings when filter type changes
	// Auto-guess runs for all types except "Exclude"
	useEffect(() => {
		if (!geoJsons || geoJsons.length === 0) {
			return;
		}

		// Clear mappings if filter type is "Exclude"
		if (filterType === "Exclude") {
			// Check if any field has a non-empty mapping
			const hasNonEmptyMappings = MAPPING_FIELDS.some((field) => hasMapping(propertyMappings[field]));

			if (hasNonEmptyMappings) {
				onMappingChange(createEmptyMappings());
			}
			// Hide auto-guess notice when switching to Exclude
			setShowAutoGuessNotice(false);
			return;
		}

		// Determine which fields can be auto-guessed
		// Auto-guess if: visible AND not user-specified AND not in pendingGlobalMappings AND (no mapping OR mapped property doesn't exist in this group)
		const canAutoGuess = (field: keyof PropertyMappings): boolean => {
			// If the field is not visible based on company settings, don't auto-guess
			if (field !== "uniqueKey") {
				const column = field as ImportTableColumn;
				const settings = company?.portalSettings?.importTableColumns?.[column];
				const visible = settings?.visible ?? DefaultImportTableColumns[column]?.visible ?? true;
				if (!visible) return false;
			}

			// If the user explicitly specified this field globally (even if empty), don't auto-guess
			if (userSpecifiedFields?.has(field)) {
				return false;
			}

			// If the user explicitly specified this field locally (at this group level), don't auto-guess
			if (localUserSpecifiedFields.current.has(field)) {
				return false;
			}

			// If there's a pending global mapping for this field, don't auto-guess
			// This prevents auto-guess from overriding global mappings (both set and explicitly cleared)
			if (pendingGlobalMappings && field !== "uniqueKey") {
				const globalValue = pendingGlobalMappings[field as keyof typeof pendingGlobalMappings];
				// undefined = never touched, null = explicitly cleared, string[] = set to value
				// Block auto-guess for both null (cleared) and string[] (set)
				if (globalValue !== undefined) {
					return false;
				}
			}

			// If the field has a mapping, check if it exists in this group's properties
			if (hasMapping(propertyMappings[field])) {
				const mappedPropertyName = propertyMappings[field]![0];
				const propertyExistsInGroup = headerProperties.some((prop) => prop.name === mappedPropertyName);
				// Only skip auto-guess if the mapped property actually exists in this group
				if (propertyExistsInGroup) {
					return false;
				}
			}

			return true;
		};

		// Check which fields we can auto-guess using a loop
		const fieldsToGuess = MAPPING_FIELDS.filter((field) => canAutoGuess(field));

		if (fieldsToGuess.length === 0) {
			return;
		}

		// Use the shared helper to get auto-guessed property mappings
		const guesses = autoGuessPropertyMappings(geoJsons);

		// Build guessed values dynamically
		// Maps field name to its guessed value (or null if not guessable/no guess found)
		const guessedValues: Partial<Record<keyof PropertyMappings, string[] | null>> = {};
		let hasNewGuesses = false;

		for (const field of fieldsToGuess) {
			// Handle the special case where salespersonMobilePhone maps to salespersonMobilePhone in guesses
			const guessKey = field as keyof typeof guesses;
			const guessValue = guesses[guessKey];

			if (guessValue) {
				guessedValues[field] = [guessValue];
				hasNewGuesses = true;
			}
		}

		if (hasNewGuesses) {
			// For each field: use guessed value if available, else pending global mapping, else existing value
			// This ensures we don't overwrite pending global mappings with null
			const mergedMappings: PropertyMappings = {} as PropertyMappings;

			for (const field of MAPPING_FIELDS) {
				const guessed = guessedValues[field];
				const pending = pendingGlobalMappings?.[field as keyof typeof pendingGlobalMappings];
				const existing = propertyMappings[field];

				// Use nullish coalescing with type narrowing - pending can be undefined (not touched) or null (explicitly cleared)
				const value = guessed ?? (pending !== undefined ? pending : existing);
				mergedMappings[field] = value as string[] | null;
			}

			onMappingChange(mergedMappings);
			// Show the auto-guess notice
			setShowAutoGuessNotice(true);
		}
	}, [
		geoJsons,
		propertyMappings,
		onMappingChange,
		filterType,
		userSpecifiedFields,
		headerProperties,
		pendingGlobalMappings,
		company,
	]);

	// Separate effect to apply pending global mappings when they change
	// This handles the case where user selects a global mapping AFTER auto-guess has run
	useEffect(() => {
		if (!pendingGlobalMappings || filterType === "Exclude") {
			return;
		}

		let hasChanges = false;
		const newMappings = { ...propertyMappings };

		for (const field of MAPPING_FIELDS) {
			const globalValue = pendingGlobalMappings[field as keyof typeof pendingGlobalMappings];
			// Only apply if the global value is defined (not undefined) and differs from current
			// undefined = never touched, null = explicitly cleared, string[] = set to value
			if (globalValue !== undefined) {
				const currentValue = propertyMappings[field];
				const globalIsNull = globalValue === null;
				const currentIsNull = currentValue === null || currentValue === undefined;
				const valuesMatch =
					globalIsNull === currentIsNull &&
					(globalIsNull || JSON.stringify(globalValue) === JSON.stringify(currentValue));

				if (!valuesMatch) {
					hasChanges = true;
					newMappings[field] = globalValue as string[] | null;
				}
			}
		}

		if (hasChanges) {
			onMappingChange(newMappings);
		}
	}, [pendingGlobalMappings, filterType, propertyMappings, onMappingChange]);

	const handleMappingChange = (field: keyof PropertyMappings, value: string | PropertyWithExamples | null) => {
		// Mark this field as user-specified locally (prevents auto-guess from overriding)
		localUserSpecifiedFields.current.add(field);

		let newValue: string[] | null = null;
		if (typeof value === "string") {
			newValue = value ? [value] : null;
		} else if (value && typeof value === "object" && "name" in value) {
			newValue = [value.name];
		}
		onMappingChange({ ...propertyMappings, [field]: newValue });
		// Hide the auto-guess notice when user manually changes any mapping
		setShowAutoGuessNotice(false);
	};

	return {
		showAutoGuessNotice,
		headerProperties,
		handleMappingChange,
		propertyMappings,
	};
}
